import { useEffect, useRef } from 'react';
import { saveMapState } from './useMapState';

interface UseMapEventHandlersProps {
  map: google.maps.Map | null;
  mapBounds: google.maps.LatLngBounds | null;
  setMapBounds: React.Dispatch<React.SetStateAction<google.maps.LatLngBounds | null>>;
  selectedMarkerId: string | null;
  initialCenterSet: React.MutableRefObject<boolean>;
  onMapBoundsChange?: (bounds: google.maps.LatLngBounds | null) => void;
  onRestaurantDeselect: () => void;
  debouncedFetchMapEntries: (bounds: google.maps.LatLngBounds | null) => void;
  initialMapCenter: google.maps.LatLngLiteral;
}

export function useMapEventHandlers({
  map,
  mapBounds,
  setMapBounds,
  selectedMarkerId,
  initialCenterSet,
  onMapBoundsChange,
  onRestaurantDeselect,
  debouncedFetchMapEntries,
  initialMapCenter,
}: UseMapEventHandlersProps) {
  const initialBoundsListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Effect to handle map drag events for deselection
  useEffect(() => {
    if (!map || !selectedMarkerId) return;

    // Deselect immediately on drag start
    const dragStartListener = map.addListener("dragstart", () => {
      // Deselect the location as soon as the user starts dragging
      onRestaurantDeselect();
    });

    // Keep a simple dragend listener for any cleanup if needed
    const dragEndListener = map.addListener("dragend", () => {
      // No additional logic needed here since deselection happens on dragstart
    });

    return () => {
      google.maps.event.removeListener(dragStartListener);
      google.maps.event.removeListener(dragEndListener);
    };
  }, [map, selectedMarkerId, onRestaurantDeselect]);

  // Effect for handling map idle state and setting initial bounds
  useEffect(() => {
    if (!map) return;

    const idleListener = map.addListener("idle", () => {
      const bounds = map.getBounds();
      const center = map.getCenter();
      const zoom = map.getZoom();

      if (bounds) {
        // Compare with previous bounds to see if they've actually changed
        if (mapBounds) {
          const prevBoundsJSON = JSON.stringify(mapBounds.toJSON());
          const newBoundsJSON = JSON.stringify(bounds.toJSON());
          const boundsChanged = prevBoundsJSON !== newBoundsJSON;
          
          // Only update mapBounds if they've actually changed
          if (boundsChanged) {
            setMapBounds(bounds);
          }
        } else {
          // First time setting bounds
          setMapBounds(bounds);
        }
        
        // Notify parent component about bounds change
        if (onMapBoundsChange) {
          onMapBoundsChange(bounds);
        }
      }

      // Save center and zoom to localStorage
      if (center && zoom && initialCenterSet.current) {
        saveMapState(center, zoom);
      }
    });

    // Function to set initial bounds if not already set
    const setInitialMapBounds = () => {
      const currentBounds = map.getBounds();
      if (currentBounds && !mapBounds) {
        setMapBounds(currentBounds);
        
        // Call onMapBoundsChange with initial bounds
        if (onMapBoundsChange) {
          onMapBoundsChange(currentBounds);
        }
        
        // Once bounds are set, remove the bounds_changed listener
        if (initialBoundsListenerRef.current) {
          google.maps.event.removeListener(initialBoundsListenerRef.current);
          initialBoundsListenerRef.current = null;
        }
      }
    };

    // Try setting initial bounds immediately
    setInitialMapBounds();

    // If bounds weren't available immediately, listen for bounds_changed
    if (!mapBounds) {
      initialBoundsListenerRef.current = map.addListener("bounds_changed", setInitialMapBounds);
    }

    // Fallback if bounds are still not set after a short delay
    if (!map.getBounds() && !mapBounds) {
      const fallbackBounds = new google.maps.LatLngBounds(
        { lat: initialMapCenter.lat - 0.1, lng: initialMapCenter.lng - 0.1 },
        { lat: initialMapCenter.lat + 0.1, lng: initialMapCenter.lng + 0.1 },
      );
      setMapBounds(fallbackBounds);
      
      // Call onMapBoundsChange with fallback bounds
      if (onMapBoundsChange) {
        onMapBoundsChange(fallbackBounds);
      }
      
      // Force a fetch of locations with the fallback bounds
      debouncedFetchMapEntries(fallbackBounds);
      
      // If we set fallback, remove the listener if it exists
      if (initialBoundsListenerRef.current) {
        google.maps.event.removeListener(initialBoundsListenerRef.current);
        initialBoundsListenerRef.current = null;
      }
    }

    // Add map click listener to deselect markers
    const mapClickListener = map.addListener("click", () => {
      // If a marker is selected, deselect it when clicking on the map
      if (selectedMarkerId) {
        onRestaurantDeselect();
      }
    });

    // Cleanup function
    return () => {
      google.maps.event.removeListener(idleListener);
      google.maps.event.removeListener(mapClickListener);
      if (initialBoundsListenerRef.current) {
        google.maps.event.removeListener(initialBoundsListenerRef.current);
      }
    };
  }, [map, mapBounds, selectedMarkerId, initialMapCenter, initialCenterSet, setMapBounds, onMapBoundsChange, onRestaurantDeselect, debouncedFetchMapEntries]);
}